/**
 * @cobuntu/widgets — same-origin authed-fetch helper (vanilla).
 *
 * Wraps `fetch` for every authed endpoint the widget bundles call
 * (`/api/users/me`, `/api/users/me/notifications/*`,
 * `/api/messaging/conversations/unread/counts`, `/api/users/me/checkout`).
 *
 * Why a helper at all: prior to v0.5.0 the IIFE bundles baked
 * `https://api.cobuntu.com` as the API base at build time and every
 * widget hit it cross-origin. That worked on `app.cobuntu.com` (cookie
 * + origin match), but on a customer apex (`belaescala.com`,
 * `foodieslx.com`) the `cobuntu_auth` cookie lives on the apex host,
 * not on `api.cobuntu.com` — so cross-origin calls reached the BE
 * anonymously and authed widgets silently rendered guest UI.
 *
 * The fix mirrors the v0.4.0 page-access change (see `pageAccess.ts`):
 * default to a **same-origin relative path** so the call routes
 * through the customer apex's reverse proxy (community-app middleware
 * + the per-route handlers under `app/api/*`), which forwards the
 * cookie to the BE server-side. The widget code stays oblivious to
 * which apex it's running on.
 *
 * Standalone non-proxied consumers (Headless SPAs hosted off-apex)
 * can still opt out via the build-time `WIDGET_API_URL` baked into
 * `__WIDGET_API_URL__` at bundle time — those callers ship their own
 * auth-cookie forwarding too, since cross-origin cookies aren't sent
 * automatically.
 */

declare const __WIDGET_AUTHED_API_URL__: string;

/**
 * Build-time override for the AUTHED API base — separate from
 * `__WIDGET_API_URL__` on purpose, and empty for every published bundle.
 *
 * It used to read `__WIDGET_API_URL__`, whose build-time default is
 * `https://api.cobuntu.com`. That made every authed widget call cross-origin,
 * and the `cobuntu_auth` cookie is host-scoped (no Domain attribute) so it is
 * never sent there. It only worked because the widget ALSO read the cookie
 * from `document.cookie` and attached a Bearer — and app platform S5 made the
 * cookie httpOnly, which ended that read and sent every call out anonymous.
 * Every widget then decided the visitor was a guest, on every domain.
 *
 * Empty means a same-origin relative path, which routes through the apex proxy
 * that forwards the cookie server-side. Only a genuinely off-apex consumer
 * sets `WIDGET_AUTHED_API_URL`, and it ships its own credential forwarding.
 */
function authedApiBase(): string {
  if (typeof __WIDGET_AUTHED_API_URL__ === "undefined") return "";
  // Trim trailing slash so callers can always concatenate `/api/...`.
  return __WIDGET_AUTHED_API_URL__.replace(/\/$/, "");
}

export interface AuthedFetchOptions extends RequestInit {
  /**
   * Optional JWT token for legacy Bearer-header sites. When the
   * widget can read the cookie via `document.cookie` (the common
   * case on a Cobuntu-proxied apex), the cookie travels via the
   * same-origin path on its own and this is unnecessary — but
   * passing it costs nothing and covers the edge case where a
   * downstream proxy strips cookies but preserves headers.
   */
  authToken?: string | null;
}

/**
 * Authenticated fetch — the canonical entry point for every widget
 * endpoint that needs the visitor's identity. Builds the URL from
 * the build-time base (or empty string for same-origin), attaches
 * the optional Bearer token, sets `credentials: "include"`, and
 * returns the raw `Response` so callers can decide how to handle
 * non-2xx (some swallow, some throw, some surface a toast).
 */
export function authedFetch(
  path: string,
  opts: AuthedFetchOptions = {},
): Promise<Response> {
  const { authToken, headers, ...rest } = opts;
  const base = authedApiBase();
  const url = `${base}${path.startsWith("/") ? path : `/${path}`}`;

  const mergedHeaders: Record<string, string> = {
    ...(headers as Record<string, string> | undefined),
  };
  if (authToken) {
    mergedHeaders["Authorization"] = `Bearer ${authToken}`;
  }

  return fetch(url, {
    ...rest,
    credentials: "include",
    headers: mergedHeaders,
  });
}
